import { useNavigate } from "react-router-dom"
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown
} from "lucide-react"
import { numberToUSD } from "../../common/utils"
import Badge from "../../common/ui/badge"
import { BillingLogListItem, InvoiceStatus } from "../../types"

const StatusBadge = ({ status }: { status: InvoiceStatus | null }) => {
  switch (status) {
    case "paid":
      return <Badge.Success>Paid</Badge.Success>
    case "issued":
      return <Badge.Alt>Issued</Badge.Alt>
    case "failed":
      return <Badge.Danger>Failed</Badge.Danger>
    case "overdue":
      return <Badge.Warning>Overdue</Badge.Warning>
    case "cancelled":
      return <Badge.Disabled>Cancelled</Badge.Disabled>
    case "refunded":
      return <Badge.Info>Refunded</Badge.Info>
    case "initial":
    case null:
    default:
      return <Badge.Info>Initial</Badge.Info>
  }
}

interface Props {
  invoices: BillingLogListItem[]
  sortDir: "asc" | "desc" | ""
  onSortAmount: () => void
}

const TH_CLASS =
  "px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider"
const TD_CLASS = "px-4 py-3 text-sm"

const BillingTable = ({ invoices, sortDir, onSortAmount }: Props) => {
  const navigate = useNavigate()

  const onClickRow = (invoice: BillingLogListItem) => {
    navigate(`/billing/${invoice.org_id}/${invoice.id}`, {
      state: { usage: invoice }
    })
  }

  return (
    <div className="bg-white px-4 py-6 sm:px-8 sm:py-12 rounded-lg overflow-x-auto">
      <table className="w-full border-collapse table-auto text-left min-w-[600px]">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className={TH_CLASS}>Org</th>
            <th className={`${TH_CLASS} hidden md:table-cell`}>Lead Total</th>
            <th className={`${TH_CLASS} hidden md:table-cell`}>Email Total</th>
            <th className={`${TH_CLASS} hidden md:table-cell`}>SMS Total</th>
            <th
              className={`${TH_CLASS} whitespace-nowrap cursor-pointer select-none hover:text-neutral-600 transition-colors`}
              onClick={onSortAmount}
            >
              Invoice amount
              {sortDir === "asc" ? (
                <ChevronUp className="inline w-3 h-3 ml-1" />
              ) : sortDir === "desc" ? (
                <ChevronDown className="inline w-3 h-3 ml-1" />
              ) : (
                <ChevronsUpDown className="inline w-3 h-3 ml-1 opacity-40" />
              )}
            </th>
            <th className={TH_CLASS}>Status</th>
            <th className={TH_CLASS}>
              <span className="sr-only">invoice link</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200">
          {invoices.map(invoice => {
            const breakdown = invoice.details?.breakdown
            const total = invoice.details?.total

            return (
              <tr
                key={invoice.id}
                onClick={() => onClickRow(invoice)}
                className="cursor-pointer hover:bg-neutral-50 transition-colors"
              >
                <td className={TD_CLASS}>{invoice.org_name}</td>
                <td className={`${TD_CLASS} hidden md:table-cell`}>
                  {breakdown?.leads?.total ?? 0}
                </td>
                <td className={`${TD_CLASS} hidden md:table-cell`}>
                  {breakdown?.emails?.total ?? 0}
                </td>
                <td className={`${TD_CLASS} hidden md:table-cell`}>
                  {breakdown?.sms?.total ?? 0}
                </td>
                <td className={TD_CLASS}>{numberToUSD(total ?? 0)}</td>
                <td className={TD_CLASS}>
                  <StatusBadge status={invoice.status} />
                </td>
                <td className={TD_CLASS}>
                  {invoice.details && <ChevronRight size={16} />}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default BillingTable
